import orderBy from 'lodash/orderBy'
import uniq from 'lodash/uniq'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import type { CartStore } from 'stores/cartStore/CartStore'
import { type ShippingOption } from 'stores/shippingStore/shipping.types'
import { type ProductArrivals } from './productArrivals.utils'

type OrderShippingOptionsByProductType = Partial<
  Record<PRODUCT_TYPES, ShippingOption>
>

type SharedShippingOption = ShippingOption & {
  orderShippingOptions: OrderShippingOptionsByProductType
}

// orderShippingOptions is optional: options read back from storage may lack it.
export type CheckoutShippingOption = ShippingOption & {
  orderShippingOptions?: OrderShippingOptionsByProductType
}

interface ProductShippingOptions {
  productType: PRODUCT_TYPES
  shippingOptions: ShippingOption[]
}

function getArrivalTime(option: ShippingOption): number {
  return new Date(option.promisedArrivalDate.iso).getTime()
}

// A shipping method is offered only when every requested product has it, dated by the
// product that arrives last.
export function mergeProductsShippingOptions(
  productsShippingOptions: ProductShippingOptions[]
): SharedShippingOption[] {
  const [firstProduct] = productsShippingOptions
  return firstProduct.shippingOptions.flatMap(({ shippingOptionName }) => {
    const productOptions: {
      productType: PRODUCT_TYPES
      shippingOption: ShippingOption
    }[] = []
    for (const { productType, shippingOptions } of productsShippingOptions) {
      const shippingOption = shippingOptions.find(
        (option) => option.shippingOptionName === shippingOptionName
      )
      if (!shippingOption) {
        return []
      }
      productOptions.push({ productType, shippingOption })
    }
    const [latestArrivingProductOption] = orderBy(
      productOptions,
      ({ shippingOption }) => getArrivalTime(shippingOption),
      'desc'
    )
    return [
      {
        ...latestArrivingProductOption.shippingOption,
        orderShippingOptions: Object.fromEntries(
          productOptions.map(({ productType, shippingOption }) => [
            productType,
            shippingOption,
          ])
        ),
      },
    ]
  })
}

export function getProductArrivals(
  shippingOption: CheckoutShippingOption
): ProductArrivals | undefined {
  const tilesShippingOption =
    shippingOption.orderShippingOptions?.[PRODUCT_TYPES.CLASSIC]
  const photoBookShippingOption =
    shippingOption.orderShippingOptions?.[PRODUCT_TYPES.PHOTO_BOOK]
  if (!tilesShippingOption || !photoBookShippingOption) {
    return undefined
  }
  return {
    tilesArrivalIso: tilesShippingOption.promisedArrivalDate.iso,
    photoBookArrivalIso: photoBookShippingOption.promisedArrivalDate.iso,
  }
}

export function getCartProductTypes(cartStore: CartStore): PRODUCT_TYPES[] {
  return uniq(cartStore.cartItems.map((cartItem) => cartItem.productType))
}

type ShippingMethodOnly = Pick<ShippingOption, 'shippingOptionName' | 'pricing'>

interface GetOrderShippingOptionParams {
  shippingOption: CheckoutShippingOption | undefined
  productType: string
  hasUpToDateDeliveryDates: boolean
}

// Each sub-order links to the promise it is sent with, which drives its production routing,
// so an order gets its own product's promise. The price stays checkout's, set per shipping method.
export function getOrderShippingOption({
  shippingOption,
  productType,
  hasUpToDateDeliveryDates,
}: GetOrderShippingOptionParams):
  | ShippingOption
  | ShippingMethodOnly
  | undefined {
  if (!shippingOption) {
    return undefined
  }
  const isCartProductType =
    productType === PRODUCT_TYPES.CLASSIC ||
    productType === PRODUCT_TYPES.PHOTO_BOOK
  const orderShippingOption =
    hasUpToDateDeliveryDates && isCartProductType
      ? shippingOption.orderShippingOptions?.[productType]
      : undefined
  // Without checkout's up-to-date dates, or for a product that joined the bag after the pay click,
  // the sub-order goes with the shipping method alone: the backend then sets its arrival date, and
  // rejects a changed bag as a price mismatch, sending the customer back to checkout.
  if (!orderShippingOption) {
    return {
      shippingOptionName: shippingOption.shippingOptionName,
      pricing: shippingOption.pricing,
    }
  }
  return { ...orderShippingOption, pricing: shippingOption.pricing }
}
